// OpenStreetMap pin picker (Leaflet, no key). Same ruby pin as the admin coordinates picker.
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { GeoPoint } from './application';

const BAGHDAD: GeoPoint = { lat: 33.3152, lng: 44.3661 };
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const PIN = L.divIcon({
  className: '',
  html: '<span style="display:block;width:30px;height:30px;background:#C8102E;border:3px solid #fff;border-radius:15px 15px 15px 2px;transform:rotate(-45deg);box-shadow:0 6px 16px rgba(200,16,46,.4)"></span>',
  iconSize: [30, 30],
  iconAnchor: [15, 36],
});

export function PinMap({ value, onChange, invalid, label }: { value: GeoPoint | null; onChange: (p: GeoPoint) => void; invalid?: boolean; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!ref.current) return;
    const map = L.map(ref.current, { center: value ?? BAGHDAD, zoom: value ? 16 : 11, scrollWheelZoom: false });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
    }).addTo(map);
    const marker = L.marker(value ?? BAGHDAD, { icon: PIN, draggable: true, keyboard: false });
    if (value) marker.addTo(map);
    const place = (p: GeoPoint) => {
      const point = { lat: round6(p.lat), lng: round6(p.lng) };
      marker.setLatLng(point);
      if (!map.hasLayer(marker)) marker.addTo(map);
      onChangeRef.current(point);
    };
    map.on('click', (e: L.LeafletMouseEvent) => place(e.latlng));
    marker.on('dragend', () => place(marker.getLatLng()));
    mapRef.current = map;
    markerRef.current = marker;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // "Use my location" or "Remove" changes the value from outside.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    if (!value) {
      marker.remove();
      return;
    }
    const cur = marker.getLatLng();
    if (map.hasLayer(marker) && cur.lat === value.lat && cur.lng === value.lng) return;
    marker.setLatLng(value);
    if (!map.hasLayer(marker)) marker.addTo(map);
    map.setView(value, Math.max(map.getZoom(), 16));
  }, [value]);

  return (
    <div
      ref={ref}
      role="application"
      aria-label={label}
      className={`relative z-0 h-[300px] overflow-hidden rounded-input border sm:h-[340px] ${invalid ? 'border-status-danger' : 'border-border-subtle'}`}
    />
  );
}
